import React, { useEffect, useState, useRef } from "react";
import Dialog from "@mui/material/Dialog";
import { Typography, notification, Progress, Button, Alert } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimesCircle } from "@fortawesome/free-regular-svg-icons";
import { InfoCircleOutlined, CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import Loading from "../../../components/Loading/LoadingEffect";
import { Link } from "react-router-dom";
import moment from "moment";
import { Form, Row, Col } from "antd";
import { drawDateTime } from "../../../controllers/V1/cms/campaignController";
const VendorListDeletePopUp = ({
  drawDateTimeOpen,
  toggleDrawPopUp,
  drawItem,
  getList,
}) => {
  const handleCancel = () => toggleDrawPopUp();
  const [form] = Form.useForm();
  const [initialValues, setInitialValues] = useState(drawItem || {});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAutoSubmit, setShowAutoSubmit] = useState(false);
  const [countdown, setCountdown] = useState(3);
  const [progress, setProgress] = useState(0);
  const timerRef = useRef(null);
  const intervalRef = useRef(null);

  const handleCancelAutoSubmit = () => {
    setShowAutoSubmit(false);
    setCountdown(3);
    setProgress(0);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  };

  const handleSubmit = async (values) => {
    try {
      setIsLoading(true);
      handleCancelAutoSubmit(); // Clear any timers
      const formattedValues = {
        ...values,
        id: drawItem?._id || undefined, 
      };
      const res = await drawDateTime(formattedValues);
      if (res.status === true) {
        notification.success({
          message: "Success",
          description: "Data successfully Drawn.",
          icon: <CheckCircleOutlined style={{ color: "green" }} />,
          duration: 2,
        });
        // navigate("/cms/campaign/list");
        toggleDrawPopUp();
        getList();
      } else {
        notification.error({
          message: "Error",
          description: res.message,
          icon: <InfoCircleOutlined style={{ color: "red" }} />,
          duration: 2,
        });
      }
    } catch (error) {
      setIsLoading(false);
      console.error("Error during submission:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Cleanup function to clear timers when component unmounts or modal closes
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (drawDateTimeOpen && drawItem) {
      // Calculate new draw date by adding 1 day
      const newDrawDate = drawItem.draw_date
        ? moment(drawItem.draw_date).add(1, "day").format("YYYY-MM-DD")
        : moment().add(1, "day").format("YYYY-MM-DD");

      const initial = {
        draw_date: newDrawDate,
        draw_time: drawItem?.draw_time || "",
      };
      
      setInitialValues(initial);
      form.setFieldsValue(initial);

      // Start the auto-submit countdown
      setShowAutoSubmit(true);
      setCountdown(3);
      setProgress(0);

      let secondsLeft = 3;
      
      // Update progress bar every 100ms for smooth animation
      intervalRef.current = setInterval(() => {
        setProgress((prev) => {
          const newProgress = prev + (100 / 30); // 30 steps in 3 seconds (100ms each)
          return newProgress >= 100 ? 100 : newProgress;
        });
      }, 100);

      // Update countdown every second
      const countdownInterval = setInterval(() => {
        secondsLeft -= 1;
        setCountdown(secondsLeft);
      }, 1000);

      // Auto-submit after 3 seconds
      timerRef.current = setTimeout(() => {
        clearInterval(intervalRef.current);
        clearInterval(countdownInterval);
        setShowAutoSubmit(false);
        form.submit(); // Auto-submit the form
      }, 3000);

      // Cleanup intervals on unmount or when dependencies change
      return () => {
        clearTimeout(timerRef.current);
        clearInterval(intervalRef.current);
        clearInterval(countdownInterval);
      };
    }
  }, [drawItem, drawDateTimeOpen, form]);

  return (
    <Dialog
      open={drawDateTimeOpen}
      onClose={handleCancel}
      fullWidth={true}
      maxWidth={"md"}
      PaperProps={{
        className: "myDialogPopUp",
      }}
    >
      <div className="myDrawer">
        <div className="inventory_popup_title text-center">
          <Typography
            variant="h5"
            component="div"
            gutterBottom
            className="mt-2 inventory_popup_title_heading"
          >
            Draw Date And Time
          </Typography>
          <Link
            to={false}
            style={{ position: "absolute", right: 8, top: 8, color: "#dd5151" }}
            onClick={handleCancel}
          >
            <FontAwesomeIcon icon={faTimesCircle} />
          </Link>
        </div>

        <div className="container-fluid">
          {showAutoSubmit && (
            <Alert
              message={
                <div>
                  <strong>Auto-submitting in {countdown} second{countdown !== 1 ? 's' : ''}...</strong>
                  <div style={{ marginTop: '10px' }}>
                    <Progress 
                      percent={Math.round(progress)} 
                      status="active" 
                      strokeColor={{
                        '0%': '#108ee9',
                        '100%': '#87d068',
                      }}
                    />
                  </div>
                  <div style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}>
                    Draw date has been updated to: <strong>{initialValues?.draw_date}</strong>
                  </div>
                </div>
              }
              type="info"
              closable
              onClose={handleCancelAutoSubmit}
              closeIcon={
                <Button 
                  size="small" 
                  danger 
                  icon={<CloseCircleOutlined />}
                >
                  Cancel Auto-Submit
                </Button>
              }
              style={{ marginBottom: '20px' }}
            />
          )}
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={initialValues}
          >
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  label="Draw Date"
                  name="draw_date"
                  rules={[
                    { required: true, message: "Please select a draw date!" },
                  ]}
                >
                  <input
                    type="date"
                    className="form-control store_input_field"
                    id="draw_date"
                    value={
                      initialValues?.draw_date
                        ? moment(initialValues.draw_date).format("YYYY-MM-DD")
                        : ""
                    }
                    onChange={(e) =>
                      form.setFieldsValue({ draw_date: e.target.value })
                    }
                  />
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item
                  label="Draw Time"
                  name="draw_time"
                  rules={[
                    { required: true, message: "Please select a draw time!" },
                  ]}
                >
                  {/* <input
                    type="time"
                    className="form-control store_input_field"
                    id="draw_time"
                    value={
                      initialValues?.draw_time || ""
                    } 
                  /> */}
                  <select
                    className="form-control store_input_field"
                    id="draw_time"
                    placeholder="Select Draw Time"
                    value={initialValues?.draw_time || undefined}
                    onChange={(value) => setInitialValues({ draw_time: value })}
                  >
                    {Array.from({ length: 24 }, (_, hour) =>
                      Array.from({ length: 60 }, (_, minute) => {
                        const time = `${hour.toString().padStart(2, "0")}:${minute
                          .toString()
                          .padStart(2, "0")}`; // Formats as "HH:MM"
                        return (
                          <option key={time} value={time}>
                            {time}
                          </option>
                        );
                      })
                    )}
                  </select>
                </Form.Item>
              </Col>
            </Row>
            <div className="mb-3 verndor_dashboard_add_edit_button text-center">
              <button
                type="submit"
                class="btn btn-primary inventory_save_button"
              >
                Save
              </button>
            </div>
          </Form>
          {error && <div style={{ color: "red" }}>{error}</div>}
        </div>
      </div>
      <Loading isLoading={isLoading} text={`Preparing download...`} />
    </Dialog>
  );
};

export default VendorListDeletePopUp;
